/**
 * Centralized configuration for all role types across industries
 * Single source of truth for role metadata, scenarios, and capabilities
 */

export interface CodingQuestionType {
  id: string;
  name: string;
}

export interface FormatOption {
  id: string;
  name: string;
  description: string;
  icon?: string;
}

export interface RoleTypeConfig {
  id: string;
  displayName: string;
  industry: string;
  description: string;
  analyticalScenarios: string[];
  codingQuestionTypes?: CodingQuestionType[];
  supportsCoding: boolean;
  supportsFinance?: boolean;
  supportsDesign?: boolean;
  supportsMarketing?: boolean;
  financeFormats?: FormatOption[];
  designFormats?: FormatOption[];
  marketingFormats?: FormatOption[];
  icon?: string;
  color?: string;
}

// Industry groupings for organization
export enum Industry {
  TECHNOLOGY = 'Technology',
  BUSINESS_CONSULTING = 'Business & Consulting',
  FINANCE = 'Finance',
  MARKETING_SALES = 'Marketing & Sales',
  DESIGN = 'Design & Creative',
  HUMAN_RESOURCES = 'Human Resources',
}

// All role type configurations
export const ROLE_TYPES: Record<string, RoleTypeConfig> = {
  // ==================== TECHNOLOGY ====================
  engineer: {
    id: 'engineer',
    displayName: 'Software Engineer',
    industry: Industry.TECHNOLOGY,
    description: 'Build and maintain software systems, write clean code, and solve technical challenges',
    analyticalScenarios: ['Code Learning', 'General Coding', 'Open-Ended Question'],
    codingQuestionTypes: [
      { id: 'algorithms', name: 'Algorithms & Data Structures' },
      { id: 'system_design', name: 'System Design' },
      { id: 'debugging', name: 'Debugging' },
    ],
    supportsCoding: true,
    color: '#3B82F6',
  },

  product_manager: {
    id: 'product_manager',
    displayName: 'Product Manager',
    industry: Industry.TECHNOLOGY,
    description: 'Define product strategy, prioritize features, and drive product development',
    analyticalScenarios: ['Product Strategy', 'User Research', 'Feature Prioritization'],
    supportsCoding: false,
    color: '#8B5CF6',
  },

  data_scientist: {
    id: 'data_scientist',
    displayName: 'Data Scientist',
    industry: Industry.TECHNOLOGY,
    description: 'Extract insights from data, build ML models, and drive data-driven decisions',
    analyticalScenarios: ['Statistical Analysis', 'ML Problem Solving', 'A/B Testing'],
    codingQuestionTypes: [
      { id: 'data_manipulation', name: 'Data Manipulation' },
      { id: 'algorithm_design', name: 'Algorithm Design' },
      { id: 'model_implementation', name: 'Model Implementation' },
    ],
    supportsCoding: true,
    color: '#10B981',
  },

  devops_engineer: {
    id: 'devops_engineer',
    displayName: 'DevOps Engineer',
    industry: Industry.TECHNOLOGY,
    description: 'Build and maintain infrastructure, automate deployments, ensure system reliability',
    analyticalScenarios: ['System Architecture', 'Incident Response', 'Automation Strategy'],
    codingQuestionTypes: [
      { id: 'infrastructure_code', name: 'Infrastructure as Code' },
      { id: 'scripting', name: 'Scripting & Automation' },
      { id: 'cicd_pipelines', name: 'CI/CD Pipelines' },
    ],
    supportsCoding: true,
    color: '#F59E0B',
  },

  // ==================== BUSINESS & CONSULTING ====================
  strategist: {
    id: 'strategist',
    displayName: 'Strategist',
    industry: Industry.BUSINESS_CONSULTING,
    description: 'Develop business strategies, analyze markets, and solve complex business problems',
    analyticalScenarios: ['Problem Decomposition', 'Open-Ended Question', 'Market Analysis'],
    supportsCoding: false,
    color: '#6366F1',
  },

  management_consultant: {
    id: 'management_consultant',
    displayName: 'Management Consultant',
    industry: Industry.BUSINESS_CONSULTING,
    description: 'Advise organizations on strategy, operations, and organizational transformation',
    analyticalScenarios: ['Case Interview', 'Market Entry Strategy', 'Cost Reduction Analysis'],
    supportsCoding: false,
    color: '#8B5CF6',
  },

  business_analyst: {
    id: 'business_analyst',
    displayName: 'Business Analyst',
    industry: Industry.BUSINESS_CONSULTING,
    description: 'Bridge business and technology, gather requirements, optimize processes',
    analyticalScenarios: ['Requirements Gathering', 'Process Optimization', 'Data Analysis'],
    supportsCoding: false,
    color: '#14B8A6',
  },

  operations_manager: {
    id: 'operations_manager',
    displayName: 'Operations Manager',
    industry: Industry.BUSINESS_CONSULTING,
    description: 'Optimize operations, manage resources, improve efficiency and quality',
    analyticalScenarios: ['Process Improvement', 'Resource Allocation', 'Supply Chain Optimization'],
    supportsCoding: false,
    color: '#F97316',
  },

  // ==================== FINANCE ====================
  analyst: {
    id: 'analyst',
    displayName: 'Financial Analyst',
    industry: Industry.FINANCE,
    description: 'Analyze financial data, build models, and provide investment recommendations',
    analyticalScenarios: ['Quantitative Analysis', 'Market Analysis', 'Open-Ended Question'],
    codingQuestionTypes: [
      { id: 'financial_data_analysis', name: 'Financial Data Analysis (Python/Excel)' },
      { id: 'sql_queries', name: 'SQL Queries' },
      { id: 'financial_calculations', name: 'Financial Calculations' },
    ],
    supportsCoding: true,
    supportsFinance: true,
    financeFormats: [
      { id: 'case_study', name: 'Case Study', description: 'Market sizing, profitability analysis, valuation scenarios' },
      { id: 'market_analysis', name: 'Market Analysis', description: 'Trend analysis, competitor research, industry dynamics' },
      { id: 'financial_modeling', name: 'Financial Modeling', description: 'DCF, comparable analysis, scenario modeling' },
    ],
    color: '#059669',
  },

  investment_banker: {
    id: 'investment_banker',
    displayName: 'Investment Banker',
    industry: Industry.FINANCE,
    description: 'Advise on M&A, raise capital, and structure complex financial transactions',
    analyticalScenarios: ['Valuation Analysis', 'M&A Strategy', 'Pitch Book Development'],
    codingQuestionTypes: [
      { id: 'financial_modeling', name: 'Financial Modeling (Python/VBA)' },
      { id: 'data_analysis', name: 'Data Analysis' },
    ],
    supportsCoding: true,
    supportsFinance: true,
    financeFormats: [
      { id: 'case_study', name: 'Case Study', description: 'M&A valuation, LBO analysis, capital raising scenarios' },
      { id: 'market_analysis', name: 'Market Analysis', description: 'Industry analysis, deal sourcing, competitive positioning' },
      { id: 'financial_modeling', name: 'Financial Modeling', description: 'LBO models, M&A models, DCF valuation' },
    ],
    color: '#0EA5E9',
  },

  quant: {
    id: 'quant',
    displayName: 'Quantitative Analyst',
    industry: Industry.FINANCE,
    description: 'Develop mathematical models for trading, risk management, and pricing',
    analyticalScenarios: ['Risk Modeling', 'Algorithmic Trading', 'Portfolio Optimization'],
    codingQuestionTypes: [
      { id: 'mathematical_algorithms', name: 'Mathematical Algorithms' },
      { id: 'statistical_computing', name: 'Statistical Computing' },
      { id: 'optimization', name: 'Optimization Problems' },
    ],
    supportsCoding: true,
    supportsFinance: true,
    financeFormats: [
      { id: 'case_study', name: 'Case Study', description: 'Risk scenarios, trading strategies, portfolio problems' },
      { id: 'market_analysis', name: 'Market Analysis', description: 'Market microstructure, volatility analysis, factor analysis' },
      { id: 'financial_modeling', name: 'Financial Modeling', description: 'Option pricing, risk models, statistical arbitrage models' },
    ],
    color: '#7C3AED',
  },

  accountant: {
    id: 'accountant',
    displayName: 'Accountant',
    industry: Industry.FINANCE,
    description: 'Prepare financial statements, ensure compliance, manage audits and tax planning',
    analyticalScenarios: ['Financial Statement Analysis', 'Audit Planning', 'Tax Strategy'],
    codingQuestionTypes: [
      { id: 'excel_automation', name: 'Excel/VBA Automation' },
      { id: 'sql_queries', name: 'SQL for Accounting' },
      { id: 'financial_calculations', name: 'Accounting Calculations' },
    ],
    supportsCoding: true,
    supportsFinance: true,
    financeFormats: [
      { id: 'case_study', name: 'Case Study', description: 'Audit scenarios, tax optimization, compliance cases' },
      { id: 'market_analysis', name: 'Market Analysis', description: 'Industry benchmarking, regulatory trends, risk assessment' },
      { id: 'financial_modeling', name: 'Financial Modeling', description: 'Tax models, budget forecasting, variance analysis' },
    ],
    color: '#84CC16',
  },

  // ==================== MARKETING & SALES ====================
  marketing_manager: {
    id: 'marketing_manager',
    displayName: 'Marketing Manager',
    industry: Industry.MARKETING_SALES,
    description: 'Develop marketing strategies, manage campaigns, and drive brand growth',
    analyticalScenarios: ['Campaign Strategy', 'Market Segmentation', 'Brand Positioning'],
    supportsCoding: false,
    supportsMarketing: true,
    marketingFormats: [
      { id: 'campaign_strategy', name: 'Campaign Strategy', description: 'Develop multi-channel marketing campaign plan' },
      { id: 'sales_roleplay', name: 'Sales Role-play', description: 'Practice discovery calls and objection handling' },
      { id: 'growth_analysis', name: 'Growth Analysis', description: 'Funnel optimization, experiment design, metrics analysis' },
    ],
    color: '#EC4899',
  },

  sales_representative: {
    id: 'sales_representative',
    displayName: 'Sales Representative',
    industry: Industry.MARKETING_SALES,
    description: 'Build relationships, close deals, and drive revenue growth',
    analyticalScenarios: ['Deal Qualification', 'Objection Handling', 'Territory Planning'],
    supportsCoding: false,
    supportsMarketing: true,
    marketingFormats: [
      { id: 'campaign_strategy', name: 'Campaign Strategy', description: 'Develop account-based marketing strategy' },
      { id: 'sales_roleplay', name: 'Sales Role-play', description: 'Practice discovery, demo, and closing conversations' },
      { id: 'growth_analysis', name: 'Growth Analysis', description: 'Pipeline analysis, forecast accuracy, conversion optimization' },
    ],
    color: '#EF4444',
  },

  growth_marketer: {
    id: 'growth_marketer',
    displayName: 'Growth Marketer',
    industry: Industry.MARKETING_SALES,
    description: 'Drive user acquisition, optimize conversion funnels, and scale growth',
    analyticalScenarios: ['Growth Experimentation', 'Funnel Optimization', 'Channel Analysis'],
    supportsCoding: false,
    supportsMarketing: true,
    marketingFormats: [
      { id: 'campaign_strategy', name: 'Campaign Strategy', description: 'Growth campaign design, channel strategy, viral loops' },
      { id: 'sales_roleplay', name: 'Sales Role-play', description: 'PLG sales, product demos, expansion conversations' },
      { id: 'growth_analysis', name: 'Growth Analysis', description: 'Experimentation framework, cohort analysis, attribution modeling' },
    ],
    color: '#F59E0B',
  },

  // ==================== DESIGN & CREATIVE ====================
  ux_designer: {
    id: 'ux_designer',
    displayName: 'UX/UI Designer',
    industry: Industry.DESIGN,
    description: 'Design user interfaces, conduct research, and create delightful experiences',
    analyticalScenarios: ['User Research', 'Design Systems', 'Usability Testing'],
    supportsCoding: false,
    supportsDesign: true,
    designFormats: [
      { id: 'portfolio_review', name: 'Portfolio Review', description: 'AI critique of design work and process discussion' },
      { id: 'design_challenge', name: 'Design Challenge', description: 'Time-boxed design problem with sketching and explanation' },
      { id: 'design_thinking', name: 'Design Thinking', description: 'Walk through design process from research to iteration' },
    ],
    color: '#A855F7',
  },

  product_designer: {
    id: 'product_designer',
    displayName: 'Product Designer',
    industry: Industry.DESIGN,
    description: 'Design end-to-end product experiences, collaborate cross-functionally',
    analyticalScenarios: ['Design Thinking', 'User Journey Mapping', 'Design Critique'],
    supportsCoding: false,
    supportsDesign: true,
    designFormats: [
      { id: 'portfolio_review', name: 'Portfolio Review', description: 'AI critique of product work and strategic thinking' },
      { id: 'design_challenge', name: 'Design Challenge', description: 'End-to-end product design challenge with user flows' },
      { id: 'design_thinking', name: 'Design Thinking', description: 'Product strategy, research synthesis, cross-functional collaboration' },
    ],
    color: '#06B6D4',
  },

  // ==================== HUMAN RESOURCES ====================
  hr_business_partner: {
    id: 'hr_business_partner',
    displayName: 'HR Business Partner',
    industry: Industry.HUMAN_RESOURCES,
    description: 'Align people strategy with business goals, drive organizational effectiveness',
    analyticalScenarios: ['Talent Strategy', 'Organization Design', 'Performance Management'],
    supportsCoding: false,
    color: '#14B8A6',
  },

  recruiter: {
    id: 'recruiter',
    displayName: 'Recruiter',
    industry: Industry.HUMAN_RESOURCES,
    description: 'Source and assess talent, build recruiting pipelines, drive hiring success',
    analyticalScenarios: ['Sourcing Strategy', 'Candidate Assessment', 'Hiring Metrics'],
    supportsCoding: false,
    color: '#F97316',
  },
};

// Helper functions
export const getRoleTypeById = (id: string): RoleTypeConfig | undefined => {
  return ROLE_TYPES[id];
};

export const getRoleTypesByIndustry = (industry: Industry): RoleTypeConfig[] => {
  return Object.values(ROLE_TYPES).filter(role => role.industry === industry);
};

export const getAllIndustries = (): Industry[] => {
  return Object.values(Industry);
};

export const getRoleTypeDisplayName = (id: string): string => {
  return ROLE_TYPES[id]?.displayName || id;
};

export const roleSupportsAnalytical = (id: string): boolean => {
  const role = ROLE_TYPES[id];
  return role ? role.analyticalScenarios.length > 0 : false;
};

export const roleSupportsCoding = (id: string): boolean => {
  const role = ROLE_TYPES[id];
  return role?.supportsCoding || false;
};

export const getAnalyticalScenariosForRole = (id: string): string[] => {
  return ROLE_TYPES[id]?.analyticalScenarios || [];
};

export const getCodingQuestionTypesForRole = (id: string): CodingQuestionType[] => {
  return ROLE_TYPES[id]?.codingQuestionTypes || [];
};

// Define available languages for each question type
const LANGUAGE_BY_QUESTION_TYPE: Record<string, Record<string, string[]>> = {
  engineer: {
    algorithms: ["Python", "JavaScript", "Java", "C++", "Go"],
    system_design: ["Python", "JavaScript", "Java", "Go"],
    debugging: ["Python", "JavaScript", "Java", "C++"]
  },
  data_scientist: {
    data_manipulation: ["Python", "R"],
    algorithm_design: ["Python", "R"],
    model_implementation: ["Python"]
  },
  devops_engineer: {
    infrastructure_code: ["Python", "Bash", "Go"],
    scripting: ["Python", "Bash", "PowerShell"],
    cicd_pipelines: ["Python", "Bash", "YAML"]
  },
  analyst: {
    financial_data_analysis: ["Python", "Excel/VBA", "SQL"],
    sql_queries: ["SQL"],
    financial_calculations: ["Python", "Excel/VBA"]
  },
  investment_banker: {
    financial_modeling: ["Python", "Excel/VBA"],
    data_analysis: ["Python", "SQL"]
  },
  quant: {
    mathematical_algorithms: ["Python", "C++", "R"],
    statistical_computing: ["Python", "R", "MATLAB"],
    optimization: ["Python", "MATLAB"]
  },
  accountant: {
    excel_automation: ["Excel/VBA", "Python"],
    sql_queries: ["SQL"],
    financial_calculations: ["Python", "Excel/VBA"]
  }
};

export const getAvailableLanguages = (roleId: string, questionTypeId: string): string[] => {
  return LANGUAGE_BY_QUESTION_TYPE[roleId]?.[questionTypeId] || ["Python"];
};

// Finance interview type helpers
export const roleSupportsFinance = (id: string): boolean => {
  const supports = ROLE_TYPES[id]?.supportsFinance || false;
  console.log(`💰 [roleSupportsFinance] Role "${id}" supports finance:`, supports);
  return supports;
};

export const getFinanceFormatsForRole = (id: string): FormatOption[] => {
  console.log(`💰 [getFinanceFormatsForRole] Looking up finance formats for role: "${id}"`);
  const roleConfig = ROLE_TYPES[id];

  if (!roleConfig) {
    console.error(`❌ [getFinanceFormatsForRole] Role "${id}" not found in ROLE_TYPES`);
    console.log('Available role keys:', Object.keys(ROLE_TYPES));
    return [];
  }

  console.log(`📦 [getFinanceFormatsForRole] Role config found:`, roleConfig);
  const formats = roleConfig.financeFormats || [];
  console.log(`💰 [getFinanceFormatsForRole] Returning ${formats.length} formats:`, formats);
  return formats;
};

// Design interview type helpers
export const roleSupportsDesign = (id: string): boolean => {
  const supports = ROLE_TYPES[id]?.supportsDesign || false;
  console.log(`🎨 [roleSupportsDesign] Role "${id}" supports design:`, supports);
  return supports;
};

export const getDesignFormatsForRole = (id: string): FormatOption[] => {
  console.log(`🎨 [getDesignFormatsForRole] Looking up design formats for role: "${id}"`);
  const roleConfig = ROLE_TYPES[id];

  if (!roleConfig) {
    console.error(`❌ [getDesignFormatsForRole] Role "${id}" not found in ROLE_TYPES`);
    console.log('Available role keys:', Object.keys(ROLE_TYPES));
    return [];
  }

  console.log(`📦 [getDesignFormatsForRole] Role config found:`, roleConfig);
  const formats = roleConfig.designFormats || [];
  console.log(`🎨 [getDesignFormatsForRole] Returning ${formats.length} formats:`, formats);
  return formats;
};

// Marketing interview type helpers
export const roleSupportsMarketing = (id: string): boolean => {
  const supports = ROLE_TYPES[id]?.supportsMarketing || false;
  console.log(`📢 [roleSupportsMarketing] Role "${id}" supports marketing:`, supports);
  return supports;
};

export const getMarketingFormatsForRole = (id: string): FormatOption[] => {
  console.log(`📢 [getMarketingFormatsForRole] Looking up marketing formats for role: "${id}"`);
  const roleConfig = ROLE_TYPES[id];

  if (!roleConfig) {
    console.error(`❌ [getMarketingFormatsForRole] Role "${id}" not found in ROLE_TYPES`);
    console.log('Available role keys:', Object.keys(ROLE_TYPES));
    return [];
  }

  console.log(`📦 [getMarketingFormatsForRole] Role config found:`, roleConfig);
  const formats = roleConfig.marketingFormats || [];
  console.log(`📢 [getMarketingFormatsForRole] Returning ${formats.length} formats:`, formats);
  return formats;
};

// Display name mapping (for backward compatibility)
export const CANDIDATE_TYPE_DISPLAY_NAMES: Record<string, string> = Object.fromEntries(
  Object.entries(ROLE_TYPES).map(([key, value]) => [key, value.displayName])
);
